import { Component, EventEmitter, Output, OnDestroy, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import jsQR from 'jsqr';

@Component({
  selector: 'app-qr-code-scanner',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatCardModule],
  templateUrl: './qr-code-scanner.component.html',
  styleUrls: ['./qr-code-scanner.component.scss']
})
export class QrCodeScannerComponent implements OnDestroy, AfterViewInit {
  @ViewChild('videoElement', { static: false }) videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement', { static: false }) canvasElement!: ElementRef<HTMLCanvasElement>;
  @Output() qrCodeScanned = new EventEmitter<string>();
  @Output() closeScanner = new EventEmitter<void>();

  error: string | null = null;
  scanning: boolean = false;
  hasBarcodeDetector: boolean = false;
  private stream: MediaStream | null = null;
  private animationId: number | null = null;

  ngAfterViewInit() {
    // Check if BarcodeDetector is available
    this.hasBarcodeDetector = 'BarcodeDetector' in window;
    this.startCamera();
  }

  ngOnDestroy() {
    this.stopCamera();
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  }

  async startCamera() {
    try {
      this.error = null;
      this.scanning = true;

      // Request camera access
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use back camera if available
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });

      if (this.videoElement && this.stream) {
        this.videoElement.nativeElement.srcObject = this.stream;
        this.videoElement.nativeElement.onloadedmetadata = () => {
          this.videoElement.nativeElement.play();
          this.startQRDetection();
        };
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      this.error = 'Não foi possível acessar a câmera. Verifique as permissões.';
      this.scanning = false;
    }
  }

  private startQRDetection() {
    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;
    const context = canvas.getContext('2d');

    if (!context) {
      this.error = 'Erro ao inicializar o scanner';
      return;
    }

    // Check if BarcodeDetector is available
    const hasBarcodeDetector = this.hasBarcodeDetector;

    const scan = async () => {
      if (video.readyState === video.HAVE_ENOUGH_DATA && this.scanning) {
        canvas.height = video.videoHeight;
        canvas.width = video.videoWidth;

        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        try {
          if (hasBarcodeDetector) {
            // Use BarcodeDetector API if available
            const barcodeDetector = new (window as any).BarcodeDetector({
              formats: ['qr_code']
            });

            const barcodes = await barcodeDetector.detect(canvas);

            if (barcodes.length > 0) {
              console.log('QR Code detected:', barcodes[0].rawValue);
              this.scanning = false;
              this.qrCodeScanned.emit(barcodes[0].rawValue);
              return;
            }
          } else {
            // Fallback: usar jsQR para decodificar QR Code real
            const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(imageData.data, canvas.width, canvas.height);
            if (code && code.data) {
              console.log('QR Code detected (jsQR):', code.data);
              this.scanning = false;
              this.qrCodeScanned.emit(code.data);
              return;
            }
          }
        } catch (error) {
          console.error('Error scanning QR code:', error);
        }
      }

      if (this.scanning) {
        this.animationId = requestAnimationFrame(scan);
      }
    };

    scan();
  }

  private stopCamera() {
    this.scanning = false;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
  }

  onClose() {
    this.stopCamera();
    setTimeout(() => {
      this.closeScanner.emit();
    });
  }

  // simulateQRScan pode ser removida em produção, mas mantida para testes se desejar
  simulateQRScan() {
    this.stopCamera();
    // ID de exemplo fornecido pelo usuário para teste
    const testId = '736C600C-3D4B-4AFD-BBB7-45826F724ACF';
    this.qrCodeScanned.emit(testId);
  }
}
